import { IStringModule } from "./IStringModule";
import { StringObject } from "../objects/StringObject";
import { StringMirrorObject } from "../objects/StringMirrorObject";
import { StringToolsContainer } from "./StringToolsContainer";
import { StringData } from "./StringData";
import { AttributeType } from "../models/attribute/AttributeType";
import { StringContext } from "./StringContext";
import { EventManager } from "./managers/EventManager";
import { AttributeMapping } from "../models/attribute/AttributeMapping";
import { ModuleLifecyclePermissions } from "../models/IModuleLifecyclePermissions";
import { StringToken } from "../models/string/StringToken";
import { CenterCache } from "./managers/CenterCache";
import { HoverTracker } from "./managers/HoverTracker";
import { ObjectManager } from "./managers/ObjectManager";
import { ScrollScopeRegistry } from "./managers/ScrollScopeRegistry";
import { ScrollMode } from "../states/ScrollState";
import { StringSignalHub, StringSignalValue } from "./managers/StringSignalHub";

const EMPTY_CONTEXT = Object.freeze({});

interface ParseContext {
  element?: HTMLElement;
  boundingRect?: DOMRect;
  viewportHeight?: number;
  baseRem?: number;
}

/**
 * Base class for a module used in the string-tune system.
 * Extend this class to create custom modules that respond to scroll, resize, input, etc.
 */
export class StringModule implements IStringModule {
  /**
   * List of attribute names this module should automatically read
   * from the DOM element and assign to the object properties.
   * Example: ["offset-top", "offset-bottom"]
   */
  protected attributesToMap: AttributeMapping[];

  /**
   * Defines CSS Custom Properties (Houdini) strictly required by this module.
   * ModuleManager will automatically register these properties on initialization
   * for Typed Object Model hardware acceleration.
   */
  public cssProperties: Array<{
    name: string;
    syntax: string;
    initialValue: string;
    inherits: boolean;
  }> = [];

  /**
   * A map that associates string keys with `StringObject` instances.
   * This map is used to manage and track `StringObject` instances on a page.
   */
  protected objectMapOnPage: Map<string, StringObject> = new Map();
  protected allObjectMapOnPage: Map<string, StringObject> = new Map();

  /**
   * A protected array that holds the collection of `StringObject` instances
   * currently present on the page.
   */
  protected objectsOnPage: StringObject[] = [];
  protected allObjectsOnPage: StringObject[] = [];

  /**
   * A map of all entered objects by their unique ID.
   */
  protected objectMap: Map<string, StringObject> = new Map();
  protected allObjectMap: Map<string, StringObject> = new Map();

  /**
   * A flat array of all connected objects.
   */
  protected objects: StringObject[] = [];
  protected allObjects: StringObject[] = [];

  protected activeObjects: Set<StringObject> = new Set();

  protected wake(object: StringObject): void {
    this.activeObjects.add(object);
  }

  protected sleep(object: StringObject): void {
    this.activeObjects.delete(object);
  }

  /**
   * The HTML attribute key that identifies objects this module is responsible for.
   */
  protected htmlKey: string = "";
  protected defaultModeScope: "all" | ScrollMode[] = "all";

  /**
   * Module type ID used internally to categorize module behavior.
   */
  protected _type: number = 1;

  /**
   * Returns the type of the module.
   * Type 1 = core module, type 2 = UI module.
   */
  public get type(): number {
    return this._type;
  }

  public get key(): string {
    return this.htmlKey;
  }

  /**
   * Tools container providing utilities for attribute parsing, unit conversion, etc.
   * Acts as a dependency injection hub for core IStringTool implementations.
   */
  protected tools: StringToolsContainer;

  /**
   * Shared global data object containing scroll state, viewport info, cursor position, etc.
   * Used for calculations within lifecycle hooks like `onScroll`, `onFrame`, and `onResize`.
   */
  protected data: StringData;

  /**
   * Configuration object specific to the current module.
   * Passed in during module registration or initialization.
   */
  protected settings: Record<string, any>;

  /**
   * Event hub for communication between modules or systems.
   * Supports custom event emitting, listening, and unsubscription.
   */
  protected events: EventManager;

  /**
   * Cache for storing and managing object centers.
   */
  protected centers: CenterCache;

  /**
   * Tracker for managing hover states of objects.
   */
  protected hover: HoverTracker;

  /**
   * Object manager for layout refreshes and in-view recalculation.
   */
  protected objectManager: ObjectManager;
  protected signals: StringSignalHub;
  protected scrollScopes: ScrollScopeRegistry;

  public permissions: ModuleLifecyclePermissions = new ModuleLifecyclePermissions();

  constructor(context: StringContext) {
    this.tools = context.tools;
    this.data = context.data;
    this.settings = context.settings;
    this.events = context.events;
    this.centers = context.centers;
    this.hover = context.hover;
    this.objectManager = context.objectManager;
    this.signals = context.signals;
    this.scrollScopes = context.scrollScopes;

    this.attributesToMap = [
      { key: "active", type: "boolean", fallback: this.settings["active"] },
      { key: "fixed", type: "boolean", fallback: this.settings["fixed"] },
      { key: "outside-container", type: "boolean", fallback: this.settings["outside-container"] },
      { key: "repeat", type: "boolean", fallback: this.settings["repeat"] },
      {
        key: "self-disable",
        type: "boolean",
        fallback: this.settings["self-disable"],
      },
      { key: "abs", type: "boolean", fallback: this.settings["abs"] },
      { key: "key", type: "string", fallback: this.settings["key"] },
      {
        key: "offset-top",
        type: "dimension",
        fallback: this.settings["offset-top"],
      },
      {
        key: "offset-bottom",
        type: "dimension",
        fallback: this.settings["offset-bottom"],
      },
      {
        key: "offset-enter",
        type: "dimension",
        fallback: this.settings["offset-enter"],
      },
      {
        key: "offset-exit",
        type: "dimension",
        fallback: this.settings["offset-exit"],
      },
      {
        key: "inview-top",
        type: "dimension",
        fallback: this.settings["inview-top"],
      },
      {
        key: "inview-bottom",
        type: "dimension",
        fallback: this.settings["inview-bottom"],
      },
      {
        key: "start",
        type: "number",
        fallback: (element: HTMLElement, object: StringObject, boundingRect: DOMRect) => {
          const top = boundingRect.top - (this.data.scroll.topPosition || 0);
          return Math.floor(top) + this.data.scroll.transformedCurrent;
        },
      },
      {
        key: "end",
        type: "number",
        fallback: (element: HTMLElement, object: StringObject, boundingRect: DOMRect) => {
          const top = boundingRect.top - (this.data.scroll.topPosition || 0);
          const height = boundingRect.height;
          return top + height - this.data.scroll.transformedCurrent;
        },
      },
      {
        key: "size",
        type: "number",
        fallback: (element: HTMLElement, object: StringObject, boundingRect: DOMRect) => {
          return boundingRect.height;
        },
      },
      {
        key: "half-width",
        type: "number",
        fallback: (element: HTMLElement, object: StringObject, boundingRect: DOMRect) => {
          return boundingRect.width / 2;
        },
      },
      {
        key: "half-height",
        type: "number",
        fallback: (element: HTMLElement, object: StringObject, boundingRect: DOMRect) => {
          return boundingRect.height / 2;
        },
      },
      { key: "enter-el", type: "string", fallback: this.settings["enter-el"] },
      { key: "enter-vp", type: "string", fallback: this.settings["enter-vp"] },
      { key: "exit-el", type: "string", fallback: this.settings["exit-el"] },
      { key: "exit-vp", type: "string", fallback: this.settings["exit-vp"] },
    ];
  }

  /**
   * Initializes a `StringObject` by mapping attributes from an HTML element
   * and applying transformations as needed.
   *
   * @param globalId - A unique identifier for the object being initialized.
   * @param object - The `StringObject` instance to be initialized.
   * @param element - The HTML element from which attributes are extracted.
   * @param attributes - A record of additional attributes to be used during initialization.
   *
   * The method performs the following steps:
   * 1. Retrieves the bounding rectangle of the provided HTML element.
   * 2. Iterates over a predefined list of attributes to map.
   * 3. Resolves fallback values for attributes, either from the provided attributes,
   *    settings, or a fallback function.
   * 4. Processes and parses the raw attribute values based on their expected type.
   * 5. Applies optional transformations to the parsed values.
   * 6. Sets the processed attributes as properties on the `StringObject` instance.
   */
  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>,
  ): void {
    let boundingRect = this.tools.boundingClientRect.process({ element });
    for (const { key, type, fallback, transform } of this.attributesToMap) {
      const resolvedFallback =
        typeof fallback === "function" ? fallback(element, object, boundingRect) : fallback;
      const attributeFallback =
        attributes[key] ?? attributes[`string-${key}`] ?? attributes[`data-string-${key}`];
      const raw = this.tools.domAttribute.process({
        element,
        key,
        fallback: attributeFallback ?? this.settings[key] ?? resolvedFallback,
      });

      let parsed = this.parseAttribute(raw, type, {
        element,
        boundingRect,
        viewportHeight: this.data.viewport.containerHeight || this.data.viewport.windowHeight,
        baseRem: this.data.viewport.baseRem,
      });

      if (transform) {
        parsed = transform(parsed);
      }
      object.setProperty(key, parsed);
    }

    this.cacheLayoutSnapshot(object, element);
  }

  protected cacheLayoutSnapshot(object: StringObject, element: HTMLElement): void {
    const host = this.data.scroll.container ?? document.body ?? document.documentElement;
    const scroller = this.data.scroll.elementContainer ?? document.documentElement;
    const rect = this.tools.transformNullify.process({ element });
    const computedStyle = window.getComputedStyle(element);
    const fallbackSize = this.getOffsetSize(element, computedStyle);

    let docLeft = 0;
    let docTop = 0;
    let width = rect.width;
    let height = rect.height;

    if (!Number.isFinite(width) || width <= 0) {
      width = fallbackSize.width;
    }
    if (!Number.isFinite(height) || height <= 0) {
      height = fallbackSize.height;
    }

    if (
      Number.isFinite(rect.left) &&
      Number.isFinite(rect.top) &&
      rect.width > 0 &&
      rect.height > 0
    ) {
      if (host === document.body || host === document.documentElement) {
        docLeft = rect.left + scroller.scrollLeft;
        docTop = rect.top + scroller.scrollTop;
      } else {
        const hostRect = host.getBoundingClientRect();
        docLeft = rect.left - hostRect.left + host.scrollLeft;
        docTop = rect.top - hostRect.top + host.scrollTop;
      }
    } else {
      const elementOffset = this.getOffsetChainPosition(element);
      if (host === document.body || host === document.documentElement) {
        docLeft = elementOffset.left;
        docTop = elementOffset.top;
      } else {
        const hostOffset = this.getOffsetChainPosition(host);
        docLeft = elementOffset.left - hostOffset.left + host.scrollLeft;
        docTop = elementOffset.top - hostOffset.top + host.scrollTop;
      }
    }

    object.setProperty<number>("layout-doc-left", docLeft);
    object.setProperty<number>("layout-doc-top", docTop);
    object.setProperty<number>("layout-width", width);
    object.setProperty<number>("layout-height", height);
    object.setProperty<string>("layout-border-radius", computedStyle.borderRadius || "0px");
  }

  private getOffsetSize(
    element: HTMLElement,
    computedStyle: CSSStyleDeclaration,
  ): { width: number; height: number } {
    const parsedWidth = parseFloat(computedStyle.width);
    const parsedHeight = parseFloat(computedStyle.height);

    return {
      width:
        element.offsetWidth ||
        element.clientWidth ||
        (Number.isFinite(parsedWidth) ? parsedWidth : 0),
      height:
        element.offsetHeight ||
        element.clientHeight ||
        (Number.isFinite(parsedHeight) ? parsedHeight : 0),
    };
  }

  private getOffsetChainPosition(element: HTMLElement): { left: number; top: number } {
    let left = 0;
    let top = 0;
    let current: HTMLElement | null = element;

    while (current) {
      left += current.offsetLeft || 0;
      top += current.offsetTop || 0;
      current = current.offsetParent as HTMLElement | null;
    }

    return { left, top };
  }

  /**
   * Calculates object-specific positions or metrics based on the current layout or scroll state.
   * This method is intended to be overridden in subclasses if the module needs to precompute
   * layout-dependent values (e.g. parallax offsets, trigger zones, distances).
   *
   * @param object The `StringObject` instance whose positions are being calculated.
   * @param windowSize The current window height or width (depending on scroll axis).
   */
  calculatePositions(object: StringObject, windowSize: number) {
    const start = object.getProperty<number>("start");
    const size = object.getProperty<number>("size");

    const offsetStart =
      object.getProperty<number>("offset-enter") ??
      object.getProperty<number>("offset-bottom");
    const offsetEnd =
      object.getProperty<number>("offset-exit") ??
      object.getProperty<number>("offset-top");

    const startElement = object.getProperty("enter-el");
    const startViewport = object.getProperty("enter-vp");
    const endElement = object.getProperty("exit-el");
    const endViewport = object.getProperty("exit-vp");

    let startPosition = -0;
    let endPosition = -0;
    let startBias = -0;
    let endBias = -0;

    if (
      (startElement === "top" && startViewport === "top") ||
      (startElement === "left" && startViewport === "left")
    ) {
      startBias = -windowSize + 1;
      startPosition = start - offsetStart;
    } else if (
      (startElement === "top" && startViewport === "bottom") ||
      (startElement === "left" && startViewport === "right")
    ) {
      startPosition = start - windowSize - offsetStart;
    } else if (
      (startElement === "bottom" && startViewport === "top") ||
      (startElement === "right" && startViewport === "left")
    ) {
      startBias = -windowSize - size + 1;
      startPosition = start + size - offsetStart;
    } else if (
      (startElement === "bottom" && startViewport === "bottom") ||
      (startElement === "right" && startViewport === "right")
    ) {
      startBias = -size + 1;
      startPosition = start - windowSize + size - offsetStart;
    }

    // End position calculation
    if (
      (endElement === "top" && endViewport === "top") ||
      (endElement === "left" && endViewport === "left")
    ) {
      endBias = -size + 1;
      endPosition = start + offsetEnd;
    } else if (
      (endElement === "top" && endViewport === "bottom") ||
      (endElement === "left" && endViewport === "right")
    ) {
      endBias = -windowSize - size + 1;
      endPosition = start - windowSize + offsetEnd;
    } else if (
      (endElement === "bottom" && endViewport === "top") ||
      (endElement === "right" && endViewport === "left")
    ) {
      endPosition = start + size + offsetEnd;
    } else if (
      (endElement === "bottom" && endViewport === "bottom") ||
      (endElement === "right" && endViewport === "right")
    ) {
      endBias = -windowSize + 1;
      endPosition = start - windowSize + size + offsetEnd;
    }

    object.setProperty<number>("start-bias", startBias);
    object.setProperty<number>("end-bias", endBias);

    object.setProperty<number>("start-position", startPosition);
    object.setProperty<number>("end-position", endPosition);
    object.setProperty<number>("difference-position", endPosition - startPosition);

    const inviewTop = object.getProperty<number>("inview-top") ?? -0;
    const inviewBottom = object.getProperty<number>("inview-bottom") ?? -0;
    object.setProperty<number>(
      "inview-start-position",
      object.getProperty<number>("start-position") + inviewTop,
    );
    object.setProperty<number>(
      "inview-end-position",
      object.getProperty<number>("end-position") + inviewBottom,
    );
  }

  /**
   * Parses a raw DOM attribute value into the correct type based on mapping.
   * Handles fallback resolution, transformation, and special units.
   *
   * @param value Raw attribute string from DOM.
   * @param type The expected attribute type (e.g. number, boolean, dimension).
   * @param context Optional helper values like element or viewport size.
   * @returns The parsed and transformed value.
   */
  protected parseAttribute(
    value: string | number | null,
    type: AttributeType,
    context: ParseContext = EMPTY_CONTEXT,
  ): any {
    if (value == null) return null;
    const valueString = typeof value === "string" ? value : String(value);

    if (typeof type === "object" && type.type === "enum") {
      return type.values.includes(valueString) ? valueString : type.values[0];
    }

    switch (type) {
      case "number":
        return typeof value === "string" ? +value || parseFloat(value) : value;

      case "boolean":
        return valueString === "" || valueString === "true";

      case "json":
        try {
          return JSON.parse(valueString);
        } catch {
          return null;
        }

      case "tuple":
        return valueString.trim().split(/\s+/);

      case "easing":
        return this.tools.easingFunction.process({ easing: valueString });

      case "color":
        return this.tools.colorParser.process({ value: valueString });

      case "dimension":
        if (value === 0 || value == "0") return 0;
        if (
          context.element != null &&
          context.viewportHeight != null &&
          context.baseRem != null &&
          context.boundingRect != null
        ) {
          return this.tools.unitParser.process({
            value,
            element: context.element,
            viewportHeight: context.viewportHeight,
            boundingRect: context.boundingRect,
            baseRem: context.baseRem,
          });
        } else {
          return 0;
        }

      case "breakpoint-dimension":
        if (
          context.element != null &&
          context.viewportHeight != null &&
          context.baseRem != null &&
          context.boundingRect != null
        ) {
          let parsedBreakpoint = valueString.trim().split("|");
          let breakpoints: Array<{ breakpoint: number; value: number }> = [];
          for (const item of parsedBreakpoint) {
            if (item.includes(":")) {
              const [key, val] = item.split(":");
              breakpoints.push({
                breakpoint: parseInt(key),
                value: this.tools.unitParser.process({
                  value: `${val}|`,
                  element: context.element,
                  viewportHeight: context.viewportHeight,
                  boundingRect: context.boundingRect,
                  baseRem: context.baseRem,
                }),
              });
            } else {
              breakpoints.push({
                breakpoint: 0,
                value: this.tools.unitParser.process({
                  value: item,
                  element: context.element,
                  viewportHeight: context.viewportHeight,
                  boundingRect: context.boundingRect,
                  baseRem: context.baseRem,
                }),
              });
            }
          }
          return breakpoints;
        }

      default:
        return value;
    }
  }

  /**
   * Determines whether the module should attach to a given object,
   * based on the presence of the module's `htmlKey` in the object keys.
   *
   * @param object The target object to test.
   * @returns `true` if the module can connect, `false` otherwise.
   */
  canConnect(object: StringObject): boolean {
    return object.keys.includes(this.htmlKey);
  }

  protected isTokenEnabledInCurrentMode(token: StringToken): boolean {
    const mode = this.data.scroll.mode;

    if (token.modeSpec.kind === "all") {
      return true;
    }

    if (token.modeSpec.kind === "include") {
      return token.modeSpec.values.includes(mode);
    }

    if (this.defaultModeScope === "all") {
      return true;
    }

    return this.defaultModeScope.includes(mode);
  }

  protected isObjectEnabledInCurrentMode(object: StringObject): boolean {
    const token = object.getToken(this.htmlKey);
    if (!token) {
      return false;
    }
    return this.isTokenEnabledInCurrentMode(token);
  }

  disconnectObject(object: StringObject): void {
    object.disconnect(this);
  }

  /**
   * Registers the module on a given object, adds the object to internal list,
   * and triggers connection logic.
   *
   * @param object The object to connect to.
   */
  connectObject(object: StringObject): void {
    const isNewConnection = object.connect(this);
    if (isNewConnection) {
      this.onObjectConnected(object);
    }
  }

  /**
   * Registers the object internally when it enters the module’s scope.
   */
  enterObject(id: string, object: StringObject): void {
    if (!this.allObjectMap.has(id)) {
      this.allObjectMap.set(id, object);
      this.allObjects.push(object);
    }

    if (this.isObjectEnabledInCurrentMode(object) && !this.objectMap.has(id)) {
      this.objectMap.set(id, object);
      this.objects.push(object);
    }
  }

  protected fastRemoveFromArray<T>(array: T[], index: number): void {
    if (index === -1) return;

    const lastIndex = array.length - 1;
    if (index !== lastIndex) {
      array[index] = array[lastIndex];
    }
    array.pop();
  }

  /**
   * Unregisters the object when it leaves the module’s scope.
   */
  exitObject(id: string): void {
    const activeObject = this.objectMap.get(id);
    if (activeObject) {
      this.objectMap.delete(id);
      const activeIndex = this.objects.indexOf(activeObject);
      this.fastRemoveFromArray(this.objects, activeIndex);
    }

    const object = this.allObjectMap.get(id);
    if (!object) return;

    this.allObjectMap.delete(id);
    const index = this.allObjects.indexOf(object);
    this.fastRemoveFromArray(this.allObjects, index);
  }

  /**
   * Adds a `StringObject` to the internal collections if it does not already exist.
   *
   * @param id - The unique identifier for the `StringObject`.
   * @param object - The `StringObject` to be added.
   *
   * @remarks
   * This method ensures that the object is added to both `objectMapOnPage` and
   * `objectsOnPage` only if the `id` is not already present in `objectMapOnPage`.
   */
  addObject(id: string, object: StringObject): void {
    if (!this.allObjectMapOnPage.has(id)) {
      this.allObjectMapOnPage.set(id, object);
      this.allObjectsOnPage.push(object);
    }

    if (this.isObjectEnabledInCurrentMode(object) && !this.objectMapOnPage.has(id)) {
      this.objectMapOnPage.set(id, object);
      this.objectsOnPage.push(object);
    }
  }

  /**
   * Removes an object from the page by its unique identifier.
   *
   * This method performs the following steps:
   * 1. Retrieves the object associated with the given `id` from `objectMapOnPage`.
   * 2. If the object is not found, the method exits early.
   * 3. Deletes the object from `objectMapOnPage`.
   * 4. Finds the index of the object in the `objectsOnPage` array.
   * 5. If the object exists in the array, it is removed using `splice`.
   *
   * @param id - The unique identifier of the object to be removed.
   */
  removeObject(id: string): void {
    const activeObject = this.objectMapOnPage.get(id);
    if (activeObject) {
      this.objectMapOnPage.delete(id);
      const activeIndex = this.objectsOnPage.indexOf(activeObject);
      this.fastRemoveFromArray(this.objectsOnPage, activeIndex);
    }

    const object = this.allObjectMapOnPage.get(id);
    if (!object) return;

    this.allObjectMapOnPage.delete(id);
    const index = this.allObjectsOnPage.indexOf(object);
    this.fastRemoveFromArray(this.allObjectsOnPage, index);

    this.exitObject(id);
    this.sleep(object);
    this.onObjectDisconnected(object);
  }

  /**
   * Called when an object is connected. Can be overridden to apply initial styles or logic.
   * @param object The connected object.
   */
  onObjectConnected(object: StringObject): void {}

  /**
   * Called when an object is disconnected. Can be overridden to clean up styles or logic.
   * @param object The disconnected object.
   */
  onObjectDisconnected(object: StringObject): void {}

  protected get respectSelfDisable(): boolean {
    return true;
  }

  protected isPrimaryElementEnabled(object: StringObject): boolean {
    return !this.respectSelfDisable || object.getProperty("self-disable") !== true;
  }

  /**
   * Applies a style or callback to both the main element and all its connected elements.
   *
   * @param object The object whose elements to update.
   * @param applyFn The function that receives an HTMLElement and performs any update.
   */
  protected applyToElementAndConnects(
    object: StringObject,
    applyFn: (el: HTMLElement) => void,
    copyFn: (el: HTMLElement, mirror?: StringMirrorObject) => void = applyFn,
  ) {
    if (this.isPrimaryElementEnabled(object)) {
      applyFn(object.htmlElement);
    }
    object.mirrorObjects.forEach((mirror) => copyFn(mirror.htmlElement, mirror));
  }

  /**
   * Directly applies a CSS variable to the object's element without closures.
   */
  protected applyVarToElement(object: StringObject, key: string, value: string | number) {
    if (this.isPrimaryElementEnabled(object)) {
      this.tools.styleTxn.setVar(object.htmlElement, key, value);
    }
  }

  /**
   * Directly applies a standard CSS property to the object's element without closures.
   */
  protected applyPropToElement(object: StringObject, key: string, value: string | number) {
    if (this.isPrimaryElementEnabled(object)) {
      this.tools.styleTxn.setProp(object.htmlElement, key, value);
    }
  }

  /**
   * Directly applies a CSS variable to all connected mirrors without closures.
   */
  protected applyVarToConnects(object: StringObject, key: string, value: string | number) {
    for (const mirror of object.mirrorObjects) {
      this.tools.styleTxn.setVar(mirror.htmlElement, key, value);
    }
  }

  /**
   * Directly applies a standard CSS property to all connected mirrors without closures.
   */
  protected applyPropToConnects(object: StringObject, key: string, value: string | number) {
    for (const mirror of object.mirrorObjects) {
      this.tools.styleTxn.setProp(mirror.htmlElement, key, value);
    }
  }

  /**
   * Returns a cached per-object event name to avoid building strings in hot paths.
   */
  protected getObjectEventName(object: StringObject, prefix: string, suffix?: string): string {
    return object.getScopedEventName(prefix, suffix);
  }

  protected emitSignal(object: StringObject, signal: string, value: StringSignalValue): void {
    object.setProperty(`signal:${signal}`, value);
    this.signals.publish(object.id, signal, value);
  }

  protected getSignal(objectId: string, signal: string): StringSignalValue | undefined {
    return this.signals.get(objectId, signal);
  }

  protected clearManagedStyles(object: StringObject): void {
    const clearStyles = (el: HTMLElement) => {
      for (let i = 0; i < this.cssProperties.length; i++) {
        el.style.removeProperty(this.cssProperties[i].name);
      }

      const key = object.getProperty<string>("key");
      if (typeof key === "string" && key.length > 0) {
        el.style.removeProperty(key);
      }
    };

    clearStyles(object.htmlElement);
    const mirrors = object.mirrorObjects;
    for (let i = 0; i < mirrors.length; i++) {
      clearStyles(mirrors[i].htmlElement);
    }
  }

  protected onObjectModeActivated(object: StringObject): void {}

  protected onObjectModeDeactivated(object: StringObject): void {
    this.clearManagedStyles(object);
  }

  protected rebuildActiveObjectsForCurrentMode(): void {
    const previousActiveOnPage = new Map(this.objectMapOnPage);

    this.objectMapOnPage = new Map();
    this.objectsOnPage = [];
    for (let i = 0; i < this.allObjectsOnPage.length; i++) {
      const object = this.allObjectsOnPage[i];
      if (!this.isObjectEnabledInCurrentMode(object)) {
        continue;
      }
      this.objectMapOnPage.set(object.id, object);
      this.objectsOnPage.push(object);
    }

    this.objectMap = new Map();
    this.objects = [];
    for (let i = 0; i < this.allObjects.length; i++) {
      const object = this.allObjects[i];
      if (!this.isObjectEnabledInCurrentMode(object)) {
        continue;
      }
      this.objectMap.set(object.id, object);
      this.objects.push(object);
    }

    previousActiveOnPage.forEach((object, id) => {
      if (!this.objectMapOnPage.has(id)) {
        this.onObjectModeDeactivated(object);
      }
    });

    this.objectMapOnPage.forEach((object, id) => {
      if (!previousActiveOnPage.has(id)) {
        this.onObjectModeActivated(object);
      }
    });
  }

  /**
   * Cleans up internal state and detaches the module from the system.
   */
  destroy(): void {
    this.objects = [];
    this.allObjects = [];
    this.objectMap = new Map();
    this.allObjectMap = new Map();
    this.objectsOnPage = [];
    this.allObjectsOnPage = [];
    this.objectMapOnPage = new Map();
    this.allObjectMapOnPage = new Map();
    this.activeObjects = new Set();
  }

  // ───────────────────────────────
  // Lifecycle Hooks
  // ───────────────────────────────

  /** Called once when the module is initialized. */
  onInit(): void {}

  /** Called once when the module is registered and can subscribe to events. */
  onSubscribe(): void {}

  /** Called once when the module is being removed and should unsubscribe. */
  onUnsubscribe(): void {}

  /** Called on each frame with current scroll and state data. */
  onFrame(data: StringData): void {}

  onMutate(data: StringData): void {}
  onScrollMeasure(data: StringData): void {}
  onMouseMoveMeasure(data: StringData): void {}
  /** Called when the window or layout is resized. */
  onResize(): void {}

  /** Called when the layout is resized width. */
  onResizeWidth(): void {}

  /** Called when DOM content changes (elements added/removed, images loaded) — no viewport remeasure. */
  onRebuild(): void {}

  /** Called when scroll position changes. */
  onScroll(data: StringData): void {}

  /** Called when user changed scroll direction. */
  onDirectionChange(): void {}

  /** Called when user starts scrolling. */
  onScrollStart(): void {}

  /** Called when user stops scrolling. */
  onScrollStop(): void {}

  /** Called when scroll direction changes (e.g., up ↔ down). */
  onScrollDirectionChange(): void {}

  /** Called when scroll axis changes (vertical ↔ horizontal). */
  onAxisChange(): void {}

  /** Called when device type changes (e.g., desktop ↔ mobile). */
  onDeviceChange(): void {}

  /** Called when scroll-related system settings or parameters change. */
  onScrollConfigChange(): void {
    this.rebuildActiveObjectsForCurrentMode();
  }

  /** Called when scroll-related system settings or parameters change. */
  onSettingsChange(): void {}

  /** Called when the DOM is rebuilt, such as after a major mutation. */
  onDOMRebuild(): void {}

  /** Called on every mouse movement. */
  onMouseMove(event: MouseEvent): void {}

  /** Called on wheel input (independent of scroll). */
  onWheel(event: WheelEvent): void {}

  /**
   * Called when DOM elements are added or removed.
   */
  onDOMMutate(added: NodeList, removed: NodeList): void {}
}
